/**
 * AccessControlGuard — keeps a logged-in session honest against the global
 * portal access policy (Maintenance Lockdown + business-hours schedule).
 *
 * Wave 3.99ad. It does NOT render anything. On a light cadence it probes
 * ``GET /system/portal-status``:
 *   - If the user is now denied, that endpoint returns 403 with a portal
 *     header and the axios interceptor (lib/api.js) hard-logs-them-out and
 *     bounces them to /login with an explanatory banner.
 *   - If still allowed (or Super Admin), it returns 200 and nothing happens.
 *
 * Cadence:
 *   - Every 60s while a non-super-admin user is logged in (catches the moment
 *     a time-based off-hours window closes even for idle users).
 *   - Immediately when the tab regains focus / becomes visible (so returning
 *     to a backgrounded tab re-checks at once).
 *
 * The instant reaction to a manual lockdown toggle is handled separately by a
 * WebSocket ``access_recheck`` broadcast (see lib/notifications.jsx); this
 * guard is the always-on safety net.
 */
import { useEffect, useRef } from 'react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';

const POLL_MS = 60_000;

export default function AccessControlGuard() {
  const { user, isSuperAdmin } = useAuth();
  const inFlightRef = useRef(false);

  useEffect(() => {
    // Only non-super-admin logged-in users can ever be locked out.
    if (!user || isSuperAdmin) return undefined;

    let cancelled = false;
    const probe = () => {
      if (cancelled || inFlightRef.current) return;
      inFlightRef.current = true;
      // The interceptor performs the hard-logout on a portal 403; here we
      // just fire the probe and swallow the result.
      api.get('/system/portal-status')
        .catch(() => {})
        .finally(() => { inFlightRef.current = false; });
    };

    const interval = setInterval(probe, POLL_MS);
    const onVisible = () => { if (document.visibilityState === 'visible') probe(); };
    window.addEventListener('focus', probe);
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      clearInterval(interval);
      window.removeEventListener('focus', probe);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [user?.id, isSuperAdmin]);

  return null;
}
